import { RouterProvider } from 'react-router';
import { UserContextProvider, useUserContext } from './context/UserContext';
import { useDevTools } from './hooks/useDevTools';
import { LoadingSpinner } from './components/LoadingSpinner';
import { LoginPage } from './components/LoginPage';
import { EmailAuthFlow } from './components/EmailAuthFlow';
import { OnboardingFlow } from './components/OnboardingFlow';
import { InternalOnboardingTour } from './components/InternalOnboardingTour';
import { ErrorBoundary } from './components/ui';
import { router } from './router';
import { isAdmin } from './lib/roles';

function CenteredLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen h-dvh bg-gradient-to-br from-orange-50 via-white to-purple-50 flex items-center justify-center p-4">
      {children}
    </div>
  );
}

function AuthRouter() {
  const {
    isLoading,
    user,
    authStep,
    pendingEmail,
    isMAppContext,
    initMAppAuth,
    requestOTP,
    verifyOTP,
    completeOnboarding,
    skipOnboarding,
    setAuthStep,
  } = useUserContext();

  // Show loading spinner during initial auth check or when waiting for MApp token
  if (isLoading && authStep === 'idle') {
    return (
      <CenteredLayout>
        <LoadingSpinner
          size="lg"
          message={isMAppContext ? 'Authenticating...' : 'Loading...'}
        />
      </CenteredLayout>
    );
  }

  // MApp authorization denied (user declined in native modal)
  if (authStep === 'mapp-denied') {
    return (
      <CenteredLayout>
        <div className="text-center space-y-4">
          <p className="text-gray-600">Authorization was denied.</p>
          <button
            onClick={initMAppAuth}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            data-testid="login-btn-mapp-retry"
          >
            Try again
          </button>
        </div>
      </CenteredLayout>
    );
  }

  // Show login page when not authenticated (desktop only)
  if (authStep === 'idle' && !user) {
    return (
      <LoginPage
        onMAppLogin={initMAppAuth}
        onEmailLogin={() => setAuthStep('email-input')}
        isLoading={isLoading}
      />
    );
  }

  // Show email auth flow
  if (authStep === 'email-input' || authStep === 'otp-verification') {
    return (
      <EmailAuthFlow
        step={authStep}
        pendingEmail={pendingEmail}
        isLoading={isLoading}
        onRequestOTP={requestOTP}
        onVerifyOTP={verifyOTP}
        onBack={() => setAuthStep('idle')}
      />
    );
  }

  // Show onboarding for first-time vendor users
  if (authStep === 'onboarding' && user) {
    return (
      <OnboardingFlow
        firstName={user.firstName}
        isLoading={isLoading}
        onComplete={completeOnboarding}
        onSkip={skipOnboarding}
      />
    );
  }

  // Show authenticated app with router
  if (authStep === 'authenticated' && user) {
    return <AuthenticatedAppWithRouter />;
  }

  // Fallback loading state
  return (
    <CenteredLayout>
      <LoadingSpinner size="lg" message="Authenticating..." />
    </CenteredLayout>
  );
}

function AuthenticatedAppWithRouter() {
  // Dev tools: Ctrl+Shift+E to export persona
  useDevTools();

  const {
    user,
    authMethod,
    showInternalOnboarding,
    completeInternalOnboarding,
    dismissOnboarding,
  } = useUserContext();

  return (
    <>
      <RouterProvider router={router} />
      {showInternalOnboarding && authMethod === 'mapp' && user && (
        <InternalOnboardingTour
          firstName={user.firstName}
          hasFullBrandAccess={isAdmin(user.roles)}
          onComplete={completeInternalOnboarding}
          onDismiss={dismissOnboarding}
        />
      )}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log to error tracking service in production
        console.error('Application error:', error, errorInfo);
      }}
    >
      <UserContextProvider>
        <AuthRouter />
      </UserContextProvider>
    </ErrorBoundary>
  );
}

export default App;
